/**
 * Google Analytics utilities for tracking page views and events
 */

// Google Analytics Measurement ID from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

/**
 * Initialize Google Analytics
 */
export const initGA = (): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('js', new Date().toISOString());
        window.gtag('config', GA_MEASUREMENT_ID, {
            send_page_view: false // We'll manually send page views
        });
    }
};

/**
 * Track page view in Google Analytics
 */
export const trackPageView = (path: string, title?: string): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_path: path,
            page_title: title || document.title
        });
    }
};

/**
 * Track custom events in Google Analytics
 */
export const trackEvent = (
    action: string,
    category: string,
    label?: string,
    value?: number
): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('event', action, {
            event_category: category,
            event_label: label,
            value: value
        });
    }
};

/**
 * Track user interactions
 */
export const trackUserInteraction = {
    // Authentication events
    login: (method: string) => trackEvent('login', 'authentication', method),
    logout: () => trackEvent('logout', 'authentication'),
    signup: (method: string) => trackEvent('sign_up', 'authentication', method),

    // Navigation events
    menuClick: (item: string) => trackEvent('menu_click', 'navigation', item),
    tabChange: (tab: string) => trackEvent('tab_change', 'navigation', tab),

    // Feature usage
    fileUpload: (fileType: string) => trackEvent('file_upload', 'feature_usage', fileType),
    integrationConnect: (vendor: string) => trackEvent('integration_connect', 'integrations', vendor),
    integrationDisconnect: (vendor: string) => trackEvent('integration_disconnect', 'integrations', vendor),

    // Canvas/Editor events
    canvasAction: (action: string) => trackEvent('canvas_action', 'canvas', action),
    editorAction: (action: string) => trackEvent('editor_action', 'editor', action),

    // Work product events
    workProductCreate: () => trackEvent('work_product_create', 'work_products'),
    workProductView: (id: string) => trackEvent('work_product_view', 'work_products', id),
    workProductEdit: (id: string) => trackEvent('work_product_edit', 'work_products', id),

    // AI/Chat events
    chatMessage: () => trackEvent('chat_message', 'ai_interaction'),
    aiAssistantUse: (feature: string) => trackEvent('ai_assistant_use', 'ai_interaction', feature),

    // Error tracking
    errorOccurred: (errorType: string, errorMessage: string) =>
        trackEvent('error', 'errors', `${errorType}: ${errorMessage}`)
};

/**
 * Track conversion events (GA4 key events)
 * These events are also tracked on the backend via Measurement Protocol,
 * but frontend tracking provides better session continuity and real-time reporting
 */
export const trackConversion = {
    /**
     * Track Terms & Conditions button click
     */
    termsButtonClicked: (tcVersion?: string) => {
        if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'terms_buttonclick', {
                tc_version: tcVersion || 'unknown',
                event_category: 'engagement'
            });
        }
    },

    /**
     * Track Terms & Conditions acceptance
     */
    termsAccepted: (
        tcVersion?: string,
        timeSpentSeconds?: number,
        attribution?: {
            gclid?: string | null;
            utm_source?: string | null;
            utm_medium?: string | null;
            utm_campaign?: string | null;
        }
    ) => {
        if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
            const eventParams: Record<string, any> = {
                tc_version: tcVersion || 'unknown',
                time_spent_seconds: timeSpentSeconds,
                event_category: 'conversion'
            };

            // Add attribution parameters if provided
            if (attribution) {
                if (attribution.gclid) {
                    eventParams.gclid = attribution.gclid; // Reserved by GA4 for attribution (not visible in custom params)
                    eventParams.google_click_id = attribution.gclid; // Custom parameter (visible in reports)
                }
                if (attribution.utm_source) eventParams.utm_source = attribution.utm_source;
                if (attribution.utm_medium) eventParams.utm_medium = attribution.utm_medium;
                if (attribution.utm_campaign) eventParams.utm_campaign = attribution.utm_campaign;
            }

            window.gtag('event', 'terms_accepted', eventParams);
        }
    },

    /**
     * Track purchase/subscription start
     */
    purchase: (transactionId: string, value: number, currency: string = 'USD', items?: any[]) => {
        if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'purchase', {
                transaction_id: transactionId,
                value: value,
                currency: currency,
                items: items,
                event_category: 'ecommerce'
            });
        }
    },

    /**
     * Track subscription start (free or paid)
     */
    subscriptionStart: (subscriptionId: string, planName: string, value?: number) => {
        if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'begin_checkout', {
                subscription_id: subscriptionId,
                plan_name: planName,
                value: value,
                currency: 'USD',
                event_category: 'ecommerce'
            });
        }
    }
};

/**
 * Track timing events (e.g., page load time, API response time)
 */
export const trackTiming = (
    category: string,
    variable: string,
    value: number,
    label?: string
): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('event', 'timing_complete', {
            event_category: category,
            name: variable,
            value: Math.round(value), // Must be an integer
            event_label: label
        });
    }
};

/**
 * Set user properties for better segmentation
 */
export const setUserProperties = (properties: Record<string, any>): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('set', GA_MEASUREMENT_ID, {
            user_properties: properties
        });
    }
};

/**
 * Track exceptions/errors
 */
export const trackException = (description: string, fatal: boolean = false): void => {
    if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('event', 'exception', {
            description: description,
            fatal: fatal
        });
    }
};